import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { AgrUser, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { FEEDADVICETYPES } from 'src/app/feed-advice/models/feedAdviceTypes';
import { SchemeSupportService } from 'src/app/feed-advice/services/scheme-support.service';
import { REGIONS } from 'src/app/shared/models/regions';

import {
  FeedAdvice,
  FeedAdviceDataSource,
  FeedAdviceType,
  FeedAdviceTypeMenuItem,
  LocationItem,
} from '../../models/feed-advice.model';
import * as actions from '../../store/feed-advice.actions';
import { upsertFeedAdviceRequest } from '../../store/feed-advice.actions';
import {
  getFeedAdvices,
  getSelectedCountry,
  getSelectedFeedAdviceTypes,
} from '../../store/feed-advice.selectors';
import { FeedAdviceCountryDialogComponent } from '../feed-advice-country-dialog/feed-advice-country-dialog.component';
import { FeedAdviceTypeDialogComponent } from '../feed-advice-type-dialog/feed-advice-type-dialog.component';

@Component({
  selector: 'app-feed-advice-overview',
  templateUrl: './feed-advice-overview.component.html',
  styleUrls: ['feed-advice-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    NgIf,
    MatSelect,
    NgFor,
    MatOption,
    TableComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FeedAdviceOverviewComponent {
  /* SELECTORS & DATA */
  selectedRegion$ = this.store.select(getSelectedCountry).pipe(skipWhile((c) => !c));
  selectedLocation$ = this.store.select(UserSelectors.selectLocation).pipe(
    skipWhile((c) => !c),
    take(1),
  );
  locations$: Observable<LocationItem[]> = this.store.select(UserSelectors.selectLocations).pipe(
    map((userlocations) =>
      userlocations.map(
        (userlocation) =>
          <LocationItem>{
            value: userlocation.id,
            name: userlocation.name,
            selected: userlocation.isSelected,
          },
      ),
    ),
  );
  regions: Array<{ id: string; value: string }> = REGIONS.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );
  feedAdviceTypes: FeedAdviceTypeMenuItem[] = FEEDADVICETYPES.sort((a, b) =>
    a.name < b.name ? -1 : 1,
  );
  feedAdviceTypesForSelector: string[] = Object.values(FeedAdviceType);
  selectedFeedAdviceTypes: string[] = Object.values(FeedAdviceType);
  selectedLocations: LocationItem[];

  /* INDICATORS */
  public isReferenceFeedAdvice: boolean;
  hasAtLeastAdministratorRole: boolean;
  hasAdministratorRole: boolean;
  hasGlobalAdministratorRole: boolean;

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  allowedToCUD$: Observable<boolean> = combineLatest([
    this.selectedRegion$,
    this.selectedLocation$,
    this.store.select(AuthorizationSelectors.hasAdministratorRole),
  ]).pipe(
    skipWhile(
      ([region, location, hasAdministratorRole]) =>
        region === null || location === null || hasAdministratorRole === null,
    ),
    map(
      ([region, location, hasAdministratorRole]) =>
        (region === location.country && hasAdministratorRole) || !hasAdministratorRole,
    ),
  );

  @ViewChild('nameTemplate') nameTemplate: TemplateRef<void>;

  columns: TableColumn[] = undefined;

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: this.columns,
    selectColumn: false,
    actionColumn: true,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
  };

  public tableConfig$: Observable<TableConfig> = combineLatest([
    this.store.select(getFeedAdvices),
    this.store.select(UserSelectors.selectLocations),
    this.store.select(UserSelectors.selectUser),
    this.allowedToCUD$,
  ]).pipe(
    skipWhile(
      ([feedAdvices, userLocations, user, allowedToCUD]) =>
        feedAdvices === null || userLocations === null || user === null || allowedToCUD === null,
    ),
    map(
      ([feedAdvices, userLocations, user, allowedToCUD]: [
        FeedAdvice[],
        SelectedLocation[],
        AgrUser,
        boolean,
      ]) => {
        this.advices = feedAdvices;
        this.userLocations = userLocations;
        this.user = user;
        const tableConfig: TableConfig = {
          ...this.tableConfigBase,
        };
        const columns = [
          {
            id: 'name',
            type: TableColumnTypes.TEMPLATE,
            template: this.nameTemplate,
            label: this.localeService.translate('feed-advice-overview-table.name'),
          },
          {
            id: 'locations',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.locations'),
          },
          {
            id: 'date',
            type: TableColumnTypes.DATE,
            label: this.localeService.translate('feed-advice-overview-table.date'),
          },
          {
            id: 'advisor',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.advisor'),
          },
          {
            id: 'type',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.type'),
          },
          {
            id: 'genders',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.genders'),
          },
          {
            id: 'feedintake',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.feedintake'),
          },
          {
            id: 'nrOfPhases',
            type: TableColumnTypes.NUMBER,
            label: this.localeService.translate('feed-advice-overview-table.nr-of-phases'),
          },
          {
            id: 'phaseTransition',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.phase-transition'),
          },
          {
            id: 'geneticLine',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.genetic-line'),
          },
          {
            id: 'bodyCondition',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.body-condition'),
          },
          {
            id: 'pigletsPerFarrowingCategory',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate(
              'feed-advice-overview-table.piglets-per-farrowing-category',
            ),
          },
          {
            id: 'weaningWeight',
            type: TableColumnTypes.NUMBER,
            label: this.localeService.translate('feed-advice-overview-table.weaning-weight'),
          },
          {
            id: 'weaningAge',
            type: TableColumnTypes.NUMBER,
            label: this.localeService.translate('feed-advice-overview-table.weaning-age'),
          },
          {
            id: 'description',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-advice-overview-table.description'),
          },
        ];
        const actions = [
          {
            id: 'edit',
            callback: (row: FeedAdvice) => this.editFeedAdvice(row.id, this.isReferenceFeedAdvice),
            label: this.localeService.translate('feed-advice-overview-table.edit'),
            icon: 'edit',
            disabled: () => !allowedToCUD,
            primary: false,
          },
          {
            id: 'copyAsNew',
            callback: (row: FeedAdvice) => this.copyAsNew(row),
            label: this.localeService.translate('feed-advice-overview-table.copy-as-new'),
            icon: 'file_copy',
            disabled: () => false,
            primary: false,
          },
          {
            id: 'copyToOtherCountry',
            callback: (row: FeedAdvice) => this.copyToOtherCountry(row),
            label: this.localeService.translate('feed-advice-overview-table.copy-to-other-country'),
            icon: 'public',
            disabled: () => !this.isReferenceFeedAdvice,
            primary: false,
          },
          {
            id: 'delete',
            callback: (row: FeedAdvice) => this.deleteFeedAdvice(row.id),
            label: this.localeService.translate('feed-advice-overview-table.delete'),
            icon: 'delete',
            disabled: () => !allowedToCUD,
            primary: false,
          },
        ];

        tableConfig.columns = columns.filter((c) =>
          this.isReferenceFeedAdvice ? c.id !== 'locations' && c.id !== 'advisor' : true,
        );
        this.columns = tableConfig.columns;
        tableConfig.actions = actions;

        if (feedAdvices) {
          this.createFeedAdviceDataSource(feedAdvices);

          // apply filter
          const filteredDataSource = this.filterDataSource(this.selectedFeedAdviceTypes);

          tableConfig.dataSource = new MatTableDataSource(filteredDataSource);

          if (!this.isReferenceFeedAdvice) {
            const feedAdviceDataSource = filteredDataSource?.filter(
              (x) =>
                userLocations
                  .filter((x) => x.isSelected)
                  .some((y) => x.locationIds.includes(y.id)) || x.locationIds.length === 0,
            );
            tableConfig.dataSource = new MatTableDataSource(feedAdviceDataSource);
          }
        } else {
          tableConfig.dataSource = new MatTableDataSource([]);
        }
        return tableConfig;
      },
    ),
  );

  advices: FeedAdvice[];
  user: AgrUser;
  userLocations: SelectedLocation[];
  feedAdviceDatasource: FeedAdviceDataSource[];

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly schemeSupportService: SchemeSupportService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialog: DialogService,
  ) {
    this.store
      .select(AuthorizationSelectors.hasAtLeastAdministratorRole)
      .pipe(
        withLatestFrom(
          this.store.select(AuthorizationSelectors.hasAdministratorRole),
          this.store.select(AuthorizationSelectors.hasGlobalAdministratorRole),
        ),
        take(1),
      )
      .subscribe(
        ([hasAtLeastAdministratorRole, hasAdministratorRole, hasGlobalAdministratorRole]) => {
          this.hasAtLeastAdministratorRole = hasAtLeastAdministratorRole;
          this.hasAdministratorRole = hasAdministratorRole;
          this.hasGlobalAdministratorRole = hasGlobalAdministratorRole;
        },
      );

    this.activatedRoute.data.subscribe((response) => {
      this.isReferenceFeedAdvice = <boolean>response.isReferenceFeedAdvice;
    });

    this.locations$.subscribe((locations) => {
      this.selectedLocations = locations.filter((l) => l.selected);
    });

    this.store
      .select(getSelectedFeedAdviceTypes)
      .pipe(skipWhile((feedAdviceTypes) => feedAdviceTypes === null))
      .subscribe((feedAdviceTypes) => {
        if (!!feedAdviceTypes && feedAdviceTypes.length > 0) {
          this.selectedFeedAdviceTypes = feedAdviceTypes;
        }
      });
  }

  translate(key: string): string {
    return this.localeService.translate(
      `${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice-overview.${key}`,
    );
  }

  // helper function to set selected Locations in the multiselect in the header.
  // We need to use a compareWith function because it does not work automatically in this case.
  areLocationsEqual(li1: LocationItem, li2: LocationItem): boolean {
    if (!li1 || !li2) {
      return false;
    }
    return li1.name === li2.name && li1.value === li2.value && li1.selected === li2.selected;
  }

  createFeedAdviceDataSource(feedAdvices: FeedAdvice[]) {
    this.feedAdviceDatasource = feedAdvices.map(
      (feedAdvice) =>
        ({
          id: feedAdvice.id,
          name: feedAdvice.name,
          locations: this.schemeSupportService.stringifyLocations(
            this.userLocations,
            feedAdvice.locationIds,
          ),
          locationIds: feedAdvice.locationIds ?? [],
          date: feedAdvice.updated,
          advisor: feedAdvice.advisorName,
          feedAdviceType: feedAdvice.type,
          type:
            feedAdvice.type !== undefined
              ? this.localeService.translate(`feedAdviceType.${feedAdvice.type.toLowerCase()}`)
              : this.localeService.translate('feedAdviceType.fattening'),
          genders: this.schemeSupportService.stringifyGenders(
            feedAdvice.isBarrow,
            feedAdvice.isBoar,
            feedAdvice.isGilt,
          ),
          feedintake:
            feedAdvice.feedIntake !== null
              ? this.localeService.translate(
                  `feed-advice-properties.feedintake.${feedAdvice.feedIntake?.toLowerCase()}`,
                )
              : '',
          nrOfPhases: feedAdvice.numberOfPhases,
          phaseTransition:
            feedAdvice.phaseTransition !== null
              ? feedAdvice.phaseTransition !== undefined
                ? this.localeService.translate(
                    `feed-advice-properties.phaseTransition.${feedAdvice.phaseTransition.toLowerCase()}`,
                  )
                : this.localeService.translate('feed-advice-properties.phaseTransition.gradual')
              : '',
          geneticLine: this.localeService.translate(feedAdvice.geneticLine),
          bodyCondition: feedAdvice.bodyCondition
            ? this.localeService.translate(
                `feed-advice-properties.body-condition.${feedAdvice.bodyCondition}`,
              )
            : '',
          pigletsPerFarrowingCategory: feedAdvice.pigletsPerFarrowingCategory,
          weaningWeight: feedAdvice.weaningWeight,
          weaningAge: feedAdvice.weaningAge,
          description: feedAdvice.description,
        }) as FeedAdviceDataSource,
    );
  }

  createFeedAdvice() {
    if (this.isReferenceFeedAdvice) {
      this.dialog
        .form(FeedAdviceTypeDialogComponent, {
          feedAdviceTypes: this.feedAdviceTypes,
          fullscreen: false,
          setPendingChanges: false,
          pushHistoryState: false,
        })
        .subscribe(({ status, result }) => {
          if (status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              actions.navigateCreateFeedAdvice({
                isReferenceFeedAdvice: this.isReferenceFeedAdvice,
                feedAdviceType: result.feedAdviceType.name,
              }),
            );
          }
        });
    } else {
      this.dialog
        .form(FeedAdviceTypeDialogComponent, {
          feedAdviceTypes: this.feedAdviceTypes,
          fullscreen: false,
          title: this.localeService.translate('select-feed-advice-type.title'),
          setPendingChanges: false,
          pushHistoryState: false,
        })
        .subscribe(({ status, result }) => {
          if (status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              actions.navigateFromReferenceRequest({ feedAdviceType: result.feedAdviceType.name }),
            );
          }
        });
    }
  }

  copyAsNew(feedAdvice: FeedAdvice) {
    const foundAdvice = this.advices.find((advice) => feedAdvice.id === advice.id);
    this.store.dispatch(
      upsertFeedAdviceRequest({
        request: {
          ...foundAdvice,
          id: 0,
          updated: null,
          advisorName: this.user.name,
        },
        isReferenceFeedAdvice: this.isReferenceFeedAdvice,
      }),
    );
  }

  copyToOtherCountry(feedAdvice: FeedAdvice) {
    if (this.isReferenceFeedAdvice) {
      let countries: string[] = [];
      if (this.hasAdministratorRole) {
        this.selectedLocation$
          .pipe(take(1))
          .subscribe(
            (location) =>
              (countries = this.regions.filter((r) => r.id === location.country).map((r) => r.id)),
          );
      }
      if (this.hasGlobalAdministratorRole) {
        countries = this.regions.map((r) => r.id);
      }
      this.dialog
        .form(FeedAdviceCountryDialogComponent, {
          countries,
          fullscreen: false,
          setPendingChanges: false,
          pushHistoryState: false,
        })
        .subscribe(({ status, result }) => {
          if (status === DIALOG_RETURN_TYPES.confirm) {
            const foundAdvice = this.advices.find((advice) => feedAdvice.id === advice.id);
            this.store.dispatch(
              upsertFeedAdviceRequest({
                request: {
                  ...foundAdvice,
                  id: 0,
                  updated: null,
                  advisorName: this.user.name,
                  country: result.country,
                },
                isReferenceFeedAdvice: this.isReferenceFeedAdvice,
              }),
            );
          }
        });
    }
  }

  viewFeedAdvice(feedAdviceId: number, isReferenceFeedAdvice: boolean) {
    this.navigateFeedadviceDetail(feedAdviceId, isReferenceFeedAdvice, 'view');
  }

  editFeedAdvice(feedAdviceId: number, isReferenceFeedAdvice: boolean) {
    this.navigateFeedadviceDetail(feedAdviceId, isReferenceFeedAdvice, 'edit');
  }

  private navigateFeedadviceDetail(
    feedAdviceId: number,
    isReferenceFeedAdvice: boolean,
    mode: string,
  ) {
    const feedadvice = this.feedAdviceDatasource.find((advice) => advice.id === feedAdviceId);
    if (
      feedadvice.feedAdviceType === FeedAdviceType.FATTENING ||
      feedadvice.feedAdviceType === FeedAdviceType.REARING ||
      feedadvice.feedAdviceType === FeedAdviceType.WEANED_PIGLETS
    ) {
      this.store.dispatch(
        actions.navigateFeedAdviceDetail({
          feedAdviceId,
          isReferenceFeedAdvice,
          mode,
          feedAdviceType: feedadvice.feedAdviceType as FeedAdviceType,
        }),
      );
    } else {
      this.store.dispatch(
        actions.navigateSowsFeedAdviceDetail({
          feedAdviceId,
          isReferenceFeedAdvice,
          mode,
          feedAdviceType: feedadvice.feedAdviceType as FeedAdviceType,
        }),
      );
    }
  }

  deleteFeedAdvice(feedAdviceId: number) {
    this.store.dispatch(
      actions.deleteFeedAdviceWithConfirmation({
        feedAdviceId,
        isReferenceFeedAdvice: this.isReferenceFeedAdvice,
      }),
    );
  }

  changeRegionSelection(selection: MatSelectChange) {
    this.store.dispatch(actions.setSelectedCountry({ save: true, id: selection.value as string }));
  }

  changeFeedAdviceTypeSelection(selection: MatSelectChange) {
    const selected = <string[]>selection.value;
    let feedAdviceDataSource = this.filterDataSource(selected);

    if (!this.isReferenceFeedAdvice) {
      feedAdviceDataSource = feedAdviceDataSource?.filter(
        (x) =>
          this.userLocations
            .filter((x) => x.isSelected)
            .some((y) => x.locationIds.includes(y.id)) || x.locationIds.length === 0,
      );
    }

    if (this.columns !== undefined) {
      let displayedColumns: TableColumn[] = this.columns;

      if (
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.FATTENING) &&
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.REARING)
      ) {
        displayedColumns = displayedColumns.filter(
          (col) => col.id !== 'genders' && col.id !== 'feedintake' && col.id !== 'phaseTransition',
        );
      }
      if (
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.FATTENING) &&
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.REARING) &&
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.WEANED_PIGLETS)
      ) {
        displayedColumns = displayedColumns.filter((col) => col.id !== 'nrOfPhases');
      }
      if (!selected.some((feedadviceType) => feedadviceType === FeedAdviceType.WEANED_PIGLETS)) {
        displayedColumns = displayedColumns.filter(
          (col) =>
            col.id !== 'geneticLine' && col.id !== 'weaningWeight' && col.id !== 'weaningAge',
        );
      }
      if (
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.SOWS_BREEDTOFARROW) &&
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.SOWS_FARROWTOWEAN) &&
        !selected.some((feedadviceType) => feedadviceType === FeedAdviceType.SOWS_WEANTOBREED)
      ) {
        displayedColumns = displayedColumns.filter(
          (col) => col.id !== 'bodyCondition' && col.id !== 'pigletsPerFarrowingCategory',
        );
      }

      // save in settings service
      this.store.dispatch(actions.setSelectedFeedadviceTypes({ save: true, types: selected }));

      if (feedAdviceDataSource) {
        this.tableConfig$ = this.tableConfig$.pipe(
          map((tableConfig) => ({
            ...tableConfig,
            columns: displayedColumns,
            dataSource: new MatTableDataSource(feedAdviceDataSource),
          })),
        );
      }
    }
  }

  filterDataSource(selected: string[]) {
    return this.feedAdviceDatasource?.filter((x) =>
      selected.some((feedadviceType) => x.feedAdviceType === feedadviceType),
    );
  }

  changeLocation(selection: MatSelectChange) {
    const selected = (<LocationItem[]>selection.value).map((val) => val.value);
    let feedAdviceDataSource: FeedAdviceDataSource[];

    if (!this.isReferenceFeedAdvice) {
      feedAdviceDataSource = this.feedAdviceDatasource?.filter(
        (x) => selected.some((id) => x.locationIds.includes(id)) || x.locationIds.length === 0,
      );
    }

    feedAdviceDataSource = feedAdviceDataSource?.filter((x) =>
      this.selectedFeedAdviceTypes.some((y) => y.includes(x.feedAdviceType)),
    );

    if (feedAdviceDataSource) {
      this.tableConfig$ = this.tableConfig$.pipe(
        map((tableConfig) => ({
          ...tableConfig,
          dataSource: new MatTableDataSource(feedAdviceDataSource),
        })),
      );
    }

    this.userLocations.forEach((user) => {
      if (selected.includes(user.id)) {
        user.isSelected = true;
      } else {
        user.isSelected = false;
      }
    });
  }
}
